import { Portal } from '@angular/cdk/portal';
import { computed, inject, Service, signal } from '@angular/core';
import { FileDocument } from '@lib/models/file-document.model';
import { EtatDer } from '@lib/models/generated/graphql';
import { DerStatus } from '@lib/models/generated/myStellium-graphql';
import { InvestisseurService } from '@lib/services/investisseur.service';
import { MyStelliumService } from '@lib/services/mystellium.service';
import { OuiSlidePanelService } from 'omnium-ui/layout';
import { filter, map, Observable, tap } from 'rxjs';

export interface DocumentRequestRecipient {
  id: number;
  name: string;
  email: string;
  accessWithMagicLink: boolean;
  derRequired: boolean;
}

@Service()
export class DocumentRequestPanelService {
  private slidePanelService = inject(OuiSlidePanelService);
  private investisseurService = inject(InvestisseurService);
  private myStelliumService = inject(MyStelliumService);
  private documentRequestPortal: Portal<any> | null = null;
  public readonly viewerClosed$: Observable<void>;
  private opened = false;

  /** Destinataires d'une demande groupée, null pour une demande sur l'investisseur courant */
  public readonly bulkRecipients = signal<DocumentRequestRecipient[] | null>(null);
  /** Investisseurs à qui le DER du consultant doit être transmis avec la demande */
  public readonly derInvestisseurIds = signal<number[]>([]);
  public readonly validatedDer = signal<FileDocument | null>(null);
  public readonly derStepRequired = computed<boolean>(() => this.derInvestisseurIds().length > 0 && !this.validatedDer());
  public readonly openingCount = signal<number>(0);

  /** Appelé avec les ids des investisseurs dont la demande groupée a été envoyée avec succès */
  private onBulkRequestsSent: ((investisseurIds: number[]) => void) | null = null;

  constructor() {
    this.viewerClosed$ = this.slidePanelService.slidePanelState$.pipe(
      filter(state => state === 'closed' && this.opened),
      tap(() => {
        this.opened = false;
      }),
      map(() => void 0)
    );
  }

  public registerPortal(portal: Portal<any>): void {
    this.documentRequestPortal = portal;
  }

  public closePanel(): void {
    this.slidePanelService.closeSlidePanel();
  }

  public openDocumentRequestViewer(): void {
    this.bulkRecipients.set(null);
    this.onBulkRequestsSent = null;
    this.resetDer(this.getCurrentInvestisseurDerIds());
    this.openPanel();
  }

  public openBulkDocumentRequestViewer(recipients: DocumentRequestRecipient[], onRequestsSent: (investisseurIds: number[]) => void): void {
    this.bulkRecipients.set(recipients);
    this.onBulkRequestsSent = onRequestsSent;
    this.resetDer(recipients.filter(recipient => recipient.derRequired).map(recipient => recipient.id));
    this.openPanel();
  }

  public validateDer(der: FileDocument): void {
    this.validatedDer.set(der);
  }

  public removeBulkRecipient(recipientId: number): void {
    this.bulkRecipients.update(recipients => recipients?.filter(recipient => recipient.id !== recipientId) ?? null);
    this.derInvestisseurIds.update(ids => ids.filter(id => id !== recipientId));
  }

  public notifyBulkRequestsSent(investisseurIds: number[]): void {
    this.onBulkRequestsSent?.(investisseurIds);
  }

  private getCurrentInvestisseurDerIds(): number[] {
    const investisseur = this.investisseurService.investisseurSig();
    const isDerCandidate =
      !!investisseur &&
      !this.investisseurService.isClient() &&
      investisseur.avancementConnaissanceClientInvestisseur?.derEtat !== EtatDer.Signe;
    if (!isDerCandidate) {
      return [];
    }
    // Sans données MyStellium, le DER est demandé : MyStellium ignore les investisseurs qui l'ont déjà validé
    const derStatus = this.myStelliumService.documentRequestData()?.derStatus ?? DerStatus.NotRequested;
    return derStatus === DerStatus.NotRequested ? [investisseur.id] : [];
  }

  private resetDer(derInvestisseurIds: number[]): void {
    this.derInvestisseurIds.set(derInvestisseurIds);
    this.validatedDer.set(null);
  }

  private openPanel(): void {
    this.openingCount.update(count => count + 1);
    this.slidePanelService.setSlidePanelContent(this.documentRequestPortal);
    this.slidePanelService.openSlidePanel({ position: 'right', autoFocus: false, disableClose: false });
  }
}
